import { initContract } from '@ts-rest/core';
import { initQueryClient } from '@ts-rest/react-query';
import Cookies from 'js-cookie';
import jwtDecode, { JwtPayload } from 'jwt-decode';

import {
  adminTsdContract,
  aiModuleContract,
  catalogsContract,
  chartContract,
  commentContract,
  customerContract,
  customsErrorContract,
  customsNotificationsContract,
  customsSimulatorContract,
  declarantContract,
  dutyContract,
  dutyExportContract,
  forwarderContract,
  invitationContract,
  madbContract,
  nomenclatureContract,
  reportsContract,
  rolesContract,
  settingsContract,
  transactionTypeContract,
  tsdContract,
  userContract,
  usersContract,
} from '@e-origin/contracts';
import { decrypt } from '@e-origin/crypto-wasm/browser-loader';
import { X_VISITOR_ID } from '@e-origin/shared';

import { ADMIN_AUTH_COOKIE_NAME, API_ENCODING_KEY, AUTH_COOKIE_NAME, MILLISECONDS_IN_SECOND, TOKEN_EXPIRATION_BUFFER_SECONDS } from '@constants';

const getBaseUrl = () =>
  `${['localhost', '0.0.0.0', '127.0.0.1'].includes(window.location.hostname) ? ((import.meta as any).env.VITE_API_URL ?? '') : ''}/api/v1`;

// eslint-disable-next-line max-statements
const apiFetcher = async ({ path, method, headers, body }: any) => {
  const isAdminRoute = window.location.pathname.includes('/admin/');
  const cookieName = isAdminRoute ? ADMIN_AUTH_COOKIE_NAME : AUTH_COOKIE_NAME;
  const redirectUrl = isAdminRoute ? '/admin/auth' : '/auth';
  const token = Cookies.get(cookieName);

  const requestHeaders: Record<string, string> = { ...headers };

  const visitorId = Cookies.get(X_VISITOR_ID);
  if (visitorId) {
    requestHeaders[X_VISITOR_ID] = visitorId;
  }

  if (token) {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.exp * MILLISECONDS_IN_SECOND < Date.now() + TOKEN_EXPIRATION_BUFFER_SECONDS * MILLISECONDS_IN_SECOND) {
        Cookies.set(cookieName, '');
      } else {
        requestHeaders.Authorization = `Bearer ${token}`;
      }
    } catch {
      Cookies.set(cookieName, '');
      window.location.href = redirectUrl;
      return { status: 401, body: null, headers: new Headers() };
    }
  }

  const response = await fetch(path, {
    method,
    headers: requestHeaders,
    body: body ?? undefined,
  });

  const responseHeaders = response.headers;
  const jwtToken = responseHeaders.get('x-jwt-token');
  if (jwtToken) {
    Cookies.set(AUTH_COOKIE_NAME, jwtToken);
  }

  let responseBody: unknown;
  const contentType = responseHeaders.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    responseBody = await response.json();
    const data = (responseBody as any)?.data;
    if (typeof data === 'string') {
      const encryptedBytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0));
      const decryptedString = await decrypt(encryptedBytes, API_ENCODING_KEY);
      const decodedData = JSON.parse(decryptedString);
      if (decodedData) {
        (responseBody as any).data = decodedData;
      }
    }
  } else {
    responseBody = await response.text();
  }

  return { status: response.status, body: responseBody, headers: responseHeaders };
};

const c = initContract();
const contract = c.router({
  admin: usersContract,
  aiModule: aiModuleContract,
  catalogs: catalogsContract,
  chart: chartContract,
  customer: customerContract,
  customsError: customsErrorContract,
  customsNotifications: customsNotificationsContract,
  declarant: declarantContract,
  duty: dutyContract,
  dutyExport: dutyExportContract,
  forwarder: forwarderContract,
  invitation: invitationContract,
  madb: madbContract,
  nomenclature: nomenclatureContract,
  reports: reportsContract,
  roles: rolesContract,
  settings: settingsContract,
  transactionType: transactionTypeContract,
  customsSimulator: customsSimulatorContract,
  user: userContract,
  tsd: tsdContract,
  comment: commentContract,
  adminTsd: adminTsdContract,
});

export const apiClient = initQueryClient(contract, {
  baseUrl: getBaseUrl(),
  api: apiFetcher,
});
